import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { api } from './api'
import { siteConfig } from './site'

type Role = 'loading' | 'guest' | 'admin' | 'error'
const SessionContext = createContext<{
  role: Role
  refresh: () => Promise<void>
  signIn: (code: string) => Promise<void>
  signOut: () => Promise<void>
} | null>(null)

export function useSession() {
  const session = useContext(SessionContext)
  if (!session) throw new Error('SessionProvider is required')
  return session
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  if (!siteConfig.adminEnabled) return <SessionContext.Provider value={visitorSession}>{children}</SessionContext.Provider>
  return <AdminSessionProvider>{children}</AdminSessionProvider>
}

const visitorSession = {
  role: 'guest' as const,
  refresh: async () => {},
  signIn: async () => { throw new Error('Administrator mode is not available on this site.') },
  signOut: async () => {},
}

function AdminSessionProvider({ children }: { children: React.ReactNode }) {
  const [role, setRole] = useState<Role>('loading')
  const [expiresAt, setExpiresAt] = useState<string>()
  const revision = useRef(0)
  const channel = useRef<BroadcastChannel | null>(null)
  const refresh = useCallback(async () => {
    const current = ++revision.current
    try {
      const session = await api.adminSession()
      if (current !== revision.current) return
      setRole(session.authenticated ? 'admin' : 'guest')
      setExpiresAt(session.expiresAt)
    } catch {
      if (current === revision.current) setRole((previous) => previous === 'loading' ? 'error' : previous)
    }
  }, [])

  useEffect(() => {
    void refresh()
    const onFocus = () => { void refresh() }
    window.addEventListener('focus', onFocus)
    const updates = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('seller-session') : null
    channel.current = updates
    if (updates) updates.onmessage = onFocus
    return () => { revision.current++; window.removeEventListener('focus', onFocus); updates?.close(); channel.current = null }
  }, [refresh])

  useEffect(() => {
    if (!expiresAt) return
    const timer = window.setTimeout(() => { void refresh() }, Math.max(0, Date.parse(expiresAt) - Date.now()) + 100)
    return () => window.clearTimeout(timer)
  }, [expiresAt, refresh])

  const signIn = async (code: string) => {
    const session = await api.signIn(code)
    revision.current++
    setRole('admin')
    setExpiresAt(session.expiresAt)
    channel.current?.postMessage('changed')
  }
  const signOut = async () => {
    await api.signOut()
    revision.current++
    setRole('guest')
    setExpiresAt(undefined)
    channel.current?.postMessage('changed')
  }
  return <SessionContext.Provider value={{ role, refresh, signIn, signOut }}>{children}</SessionContext.Provider>
}
